<template>
  <div class="black-order-page">
    <header class="brand-hero">
      <div class="brand-copy">
        <h1>{{ storeName }}</h1>
        <p>{{ storeSlogan }}</p>
        <button type="button" @click="openOrders">查看我的订单 <span>›</span></button>
      </div>
      <div class="recommend-banner">
        <img :src="heroImage" alt="" />
        <div>
          <strong>今日推荐</strong>
          <span>Today's Recommendation</span>
        </div>
      </div>
    </header>

    <div class="search-box">
      <span class="search-icon" aria-hidden="true"></span>
      <input v-model.trim="searchKeyword" type="search" placeholder="搜索你感兴趣的美食吧～" />
    </div>

    <main class="menu-layout">
      <aside class="category-list">
        <button
          v-for="category in categoryList"
          :key="category.id"
          type="button"
          :class="{ active: activeCategoryId === category.id }"
          @click="selectCategory(category.id)"
        >
          <span>{{ category.name }}</span>
          <i v-if="category.count">{{ category.count }}</i>
        </button>
      </aside>

      <section class="goods-panel">
        <div class="category-heading">
          <strong>{{ activeCategoryName }}</strong>
          <span>Selected dishes</span>
        </div>

        <div v-if="loading" class="state-message">菜品加载中...</div>
        <div v-else-if="!visibleGoods.length" class="state-message">暂无相关菜品</div>
        <template v-else>
          <article v-for="goods in visibleGoods" :key="goods.Id" class="goods-card">
            <div class="goods-image">
              <img :src="goods.PicUrl || placeholderImage" :alt="goods.GoodsName" />
              <span v-if="goods.IsRecommend" class="recommend-tag">招牌菜</span>
            </div>
            <div class="goods-info">
              <div>
                <strong>{{ goods.GoodsName }}</strong>
                <span>{{ goods.Description || goods.Unit || "精选菜品" }}</span>
              </div>
              <div class="goods-action">
                <p>¥{{ formatMoney(getGoodsPrice(goods)) }}<small>/{{ goods.Unit || "份" }}</small></p>
                <button
                  v-if="needsSpecification(goods)"
                  type="button"
                  @click="openSpecification(goods)"
                >
                  选规格
                </button>
                <button v-else type="button" class="add-button" @click="addSimpleGoods(goods)">＋</button>
              </div>
            </div>
          </article>
        </template>
      </section>
    </main>

    <footer class="cart-bar" @click="showCartPopup = true">
      <div class="cart-symbol">
        <span>♨</span>
        <i v-if="cartTotalCount">{{ cartTotalCount }}</i>
      </div>
      <div class="cart-summary">
        <span v-if="!cartTotalCount">空空如也，快来选购吧</span>
        <template v-else>
          <strong>¥{{ formatMoney(cartTotalMoney) }}</strong>
          <span>共 {{ cartTotalCount }} 份</span>
        </template>
      </div>
      <button type="button" @click.stop="handleSubmit">去下单</button>
    </footer>

    <van-popup v-model="showCartPopup" class="black-popup" position="bottom" round>
      <section class="cart-popup">
        <header class="popup-header">
          <strong>已选菜品</strong>
          <button type="button" :disabled="!cartItems.length" @click="clearCart">一键清空</button>
        </header>
        <div class="cart-list">
          <div v-if="!cartItems.length" class="state-message">购物车还是空的</div>
          <div v-for="item in cartItems" :key="item.key" class="cart-item">
            <img :src="item.image || placeholderImage" alt="" />
            <div class="cart-item-info">
              <strong>{{ item.name }}</strong>
              <span>{{ item.specName }}{{ item.remarkText ? ` · ${item.remarkText}` : "" }}</span>
              <p>¥{{ formatMoney(item.price) }}</p>
            </div>
            <div class="quantity-control">
              <button type="button" @click="changeCartQuantity(item.key, -1)">−</button>
              <span>{{ item.quantity }}</span>
              <button type="button" @click="changeCartQuantity(item.key, 1)">＋</button>
            </div>
          </div>
        </div>
        <div class="cart-total">
          <span>共 {{ cartTotalCount }} 份</span>
          <strong>合计 ¥{{ formatMoney(cartTotalMoney) }}</strong>
        </div>
        <footer class="popup-footer">
          <button type="button" class="close-button" @click="showCartPopup = false">关闭</button>
          <button type="button" class="submit-button" :disabled="!cartItems.length" @click="handleSubmit">
            去提交
          </button>
        </footer>
      </section>
    </van-popup>

    <van-popup v-model="showSpecificationPopup" class="black-popup" position="bottom" round>
      <section v-if="editingGoods" class="spec-popup">
        <header class="popup-header">
          <div>
            <strong>{{ editingGoods.GoodsName }}</strong>
            <span>请选择规格和备注</span>
          </div>
          <button type="button" @click="closeSpecification">×</button>
        </header>

        <div class="spec-content">
          <div class="option-group">
            <h3>大小份</h3>
            <div class="option-grid">
              <button
                v-for="spec in specificationOptions"
                :key="spec.id"
                type="button"
                :class="{ active: selectedSpecificationId === spec.id }"
                @click="selectedSpecificationId = spec.id"
              >
                {{ spec.name }} <span>¥{{ formatMoney(spec.price) }}</span>
              </button>
            </div>
          </div>
          <div v-if="remarkOptions.length" class="option-group">
            <h3>备注信息</h3>
            <div class="option-grid">
              <button
                v-for="remark in remarkOptions"
                :key="remark"
                type="button"
                :class="{ active: selectedRemarks.includes(remark) }"
                @click="toggleRemark(remark)"
              >
                {{ remark }}
              </button>
            </div>
          </div>
          <div class="option-group">
            <h3>手动备注</h3>
            <textarea
              v-model.trim="customRemark"
              maxlength="80"
              placeholder="请输入口味、忌口等备注信息"
            ></textarea>
          </div>
        </div>
        <footer class="spec-footer">
          <div>
            <span>单价</span>
            <strong>¥{{ formatMoney(selectedSpecificationPrice) }}</strong>
          </div>
          <button type="button" @click="confirmSpecification">加入购物车</button>
        </footer>
      </section>
    </van-popup>
  </div>
</template>

<script>
import { getFoodList } from "@/api/foodOrder";
import { handleSessionStorage } from "@/utils";

export default {
  name: "BlackMicropoint",
  data() {
    return {
      loading: false,
      storeName: "V汤泉生活馆（北京）",
      storeSlogan: "食有风骨，味见匠心",
      heroImage: require("@/assets/order/1@2x(20).png"),
      placeholderImage: require("@/assets/order/1@2x(20).png"),
      searchKeyword: "",
      activeCategoryId: null,
      categoryGroups: [],
      cartItems: [],
      showCartPopup: false,
      showSpecificationPopup: false,
      editingGoods: null,
      selectedSpecificationId: null,
      selectedRemarks: [],
      customRemark: "",
      previousPageChromeTheme: null,
    };
  },
  computed: {
    /**
     * 将接口分类转换为左侧导航需要的数据，并实时计算每个分类在购物车中的数量。
     */
    categoryList() {
      return this.categoryGroups.map((group) => ({
        id: group.GoodsCatalogId,
        name: group.GoodsCatalogName,
        count: this.cartItems
          .filter((item) => item.catalogId === group.GoodsCatalogId)
          .reduce((total, item) => total + item.quantity, 0),
      }));
    },
    /**
     * 获取当前分类名称，作为右侧菜品列表标题。
     */
    activeCategoryName() {
      const category = this.categoryList.find(
        (item) => item.id === this.activeCategoryId
      );
      return category ? category.name : "精选菜品";
    },
    /**
     * 根据当前分类和搜索关键词过滤菜品。
     * 搜索时跨分类查询，未搜索时只显示左侧当前选中的分类。
     */
    visibleGoods() {
      const keyword = this.searchKeyword.toLowerCase();
      const groups = keyword
        ? this.categoryGroups
        : this.categoryGroups.filter(
            (group) => group.GoodsCatalogId === this.activeCategoryId
          );
      return groups.reduce((result, group) => {
        const goods = (group.Goods || []).filter((item) =>
          String(item.GoodsName || "")
            .toLowerCase()
            .includes(keyword)
        );
        return result.concat(goods);
      }, []);
    },
    /**
     * 汇总购物车全部条目的数量。
     */
    cartTotalCount() {
      return this.cartItems.reduce((total, item) => total + item.quantity, 0);
    },
    /**
     * 汇总购物车金额，保留原始数字供界面统一格式化。
     */
    cartTotalMoney() {
      return this.cartItems.reduce(
        (total, item) => total + item.price * item.quantity,
        0
      );
    },
    /**
     * 读取当前编辑菜品的规格列表。
     * 接口没有规格时生成“标准份”，保证所有菜品都能通过新规格弹窗加入购物车。
     */
    specificationOptions() {
      if (!this.editingGoods) return [];
      const specifications = this.editingGoods.Specifications || [];
      if (specifications.length) {
        return specifications.map((item) => ({
          id: String(item.Id),
          name: item.Name || item.SpecificName || "标准份",
          price: Number(item.Price || this.editingGoods.Price || 0),
        }));
      }
      return [
        {
          id: "default",
          name: "标准份",
          price: Number(this.editingGoods.Price || 0),
        },
      ];
    },
    /**
     * 合并接口提供的口味、做法和商品备注，去重后供备注按钮选择。
     */
    remarkOptions() {
      if (!this.editingGoods) return [];
      return [
        ...(this.editingGoods.TasteArray || []),
        ...(this.editingGoods.MethodArray || []),
        ...(this.editingGoods.GoodsRemarkArray || []),
      ].filter((item, index, array) => item && array.indexOf(item) === index);
    },
    /**
     * 获取当前选中规格的单价。
     */
    selectedSpecificationPrice() {
      const specification = this.specificationOptions.find(
        (item) => item.id === this.selectedSpecificationId
      );
      return specification ? specification.price : 0;
    },
  },
  created() {
    this.applyPageChromeTheme();
    this.initializePage();
  },
  beforeDestroy() {
    this.restorePageChromeTheme();
  },
  methods: {
    /**
     * 将当前 H5 页面的浏览器外观切换成深色主题。
     * theme-color 会影响支持该标准的 Android 微信顶部栏；color-scheme 和
     * apple-mobile-web-app-status-bar-style 用于支持对应能力的 WebKit 环境。
     * 同时设置 html/body 背景，可以消除页面回弹、刘海安全区露出的白色区域。
     *
     * 微信公众号的原生标题栏由微信客户端控制，部分 iOS 微信版本会忽略这些
     * Web 标准；该方法是在公众号 H5 权限范围内能够实现的最完整适配。
     */
    applyPageChromeTheme() {
      const themeColorMeta = this.getOrCreateMeta("name", "theme-color");
      const statusBarMeta = this.getOrCreateMeta(
        "name",
        "apple-mobile-web-app-status-bar-style"
      );
      const colorSchemeMeta = this.getOrCreateMeta("name", "color-scheme");

      this.previousPageChromeTheme = {
        themeColor: themeColorMeta.getAttribute("content") || "",
        statusBar: statusBarMeta.getAttribute("content") || "",
        colorScheme: colorSchemeMeta.getAttribute("content") || "",
        htmlBackground: document.documentElement.style.backgroundColor,
        bodyBackground: document.body.style.backgroundColor,
      };

      themeColorMeta.setAttribute("content", "#171717");
      statusBarMeta.setAttribute("content", "black-translucent");
      colorSchemeMeta.setAttribute("content", "dark");
      document.documentElement.style.backgroundColor = "#171717";
      document.body.style.backgroundColor = "#171717";
    },
    /**
     * 获取指定 meta；页面原本不存在时创建并追加到 head。
     * 集中封装可以避免主题初始化中重复编写 DOM 查询和创建逻辑。
     *
     * @param {string} attribute 用于定位 meta 的属性名，例如 name。
     * @param {string} value 属性值，例如 theme-color。
     * @returns {HTMLMetaElement} 已存在或新创建的 meta 元素。
     */
    getOrCreateMeta(attribute, value) {
      let meta = document.head.querySelector(`meta[${attribute}="${value}"]`);
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attribute, value);
        document.head.appendChild(meta);
      }
      return meta;
    },
    /**
     * 离开黑色点餐页时恢复进入前的浏览器主题和页面背景。
     * 这样深色配置只服务于 BlackMicropoint，不会导致项目其他浅色页面变黑。
     */
    restorePageChromeTheme() {
      if (!this.previousPageChromeTheme) {
        return;
      }
      this.getOrCreateMeta("name", "theme-color").setAttribute(
        "content",
        this.previousPageChromeTheme.themeColor
      );
      this.getOrCreateMeta(
        "name",
        "apple-mobile-web-app-status-bar-style"
      ).setAttribute("content", this.previousPageChromeTheme.statusBar);
      this.getOrCreateMeta("name", "color-scheme").setAttribute(
        "content",
        this.previousPageChromeTheme.colorScheme
      );
      document.documentElement.style.backgroundColor =
        this.previousPageChromeTheme.htmlBackground;
      document.body.style.backgroundColor =
        this.previousPageChromeTheme.bodyBackground;
    },
    /**
     * 初始化门店文案并请求分类和菜品。
     * code 优先读取 saasCode，其次读取 code；另外两个参数直接透传给 getFoodList。
     */
    initializePage() {
      const query = this.$route && this.$route.query ? this.$route.query : {};
      const storedVpn = handleSessionStorage.get("vpnInfo") || {};
      const storedDiningRoomId = handleSessionStorage.get("diningRoomId") || "";
      this.storeName = query.storeName || storedVpn.name || this.storeName;
      this.storeSlogan = query.storeSlogan || this.storeSlogan;
      this.loadFoodList({
        code: query.saasCode || query.code || storedVpn.code || "",
        diningRoomId: query.diningRoomId || storedDiningRoomId,
        vpnId: query.vpnId || storedVpn.id || "",
      });
    },
    /**
     * 调用 getFoodList 获取动态分类和菜品。
     * 为每个菜品补充所属分类 id，便于购物车统计；接口失败时清空列表并提示用户。
     *
     * @param {Object} params getFoodList 接口参数。
     */
    async loadFoodList(params) {
      if (!params.code) {
        this.categoryGroups = [];
        return;
      }

      console.log("loadFoodList ==== " +JSON.stringify(params))
      this.loading = true;
      try {
        const response = await getFoodList(params);
        this.categoryGroups = (response || []).map((group) => ({
          ...group,
          Goods: (group.Goods || []).map((goods) => ({
            ...goods,
            GoodsCatalogId: group.GoodsCatalogId,
          })),
        }));
        this.activeCategoryId = this.categoryGroups.length
          ? this.categoryGroups[0].GoodsCatalogId
          : null;
      } catch (error) {
        this.categoryGroups = [];
        this.$toast("菜品加载失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
    /**
     * 切换左侧菜品分类。
     *
     * @param {string|number} categoryId 分类唯一标识。
     */
    selectCategory(categoryId) {
      this.searchKeyword = "";
      this.activeCategoryId = categoryId;
    },
    /**
     * 判断菜品是否需要打开新规格弹窗。
     * 多规格、口味、做法或预设备注任意一项存在时均进入规格选择流程。
     */
    needsSpecification(goods) {
      return Boolean(
        goods.IsMultiSpecific ||
          (goods.Specifications && goods.Specifications.length) ||
          (goods.TasteArray && goods.TasteArray.length) ||
          (goods.MethodArray && goods.MethodArray.length) ||
          (goods.GoodsRemarkArray && goods.GoodsRemarkArray.length)
      );
    },
    /**
     * 获取菜品展示价格；多规格菜品显示最低规格价。
     */
    getGoodsPrice(goods) {
      const prices = (goods.Specifications || []).map((item) =>
        Number(item.Price || 0)
      );
      return prices.length ? Math.min(...prices) : Number(goods.Price || 0);
    },
    /**
     * 打开独立规格弹窗并重置上一次编辑内容。
     */
    openSpecification(goods) {
      this.editingGoods = goods;
      this.selectedRemarks = [];
      this.customRemark = "";
      this.$nextTick(() => {
        const firstSpecification = this.specificationOptions[0];
        this.selectedSpecificationId = firstSpecification
          ? firstSpecification.id
          : null;
      });
      this.showSpecificationPopup = true;
    },
    /**
     * 关闭规格弹窗并清理临时编辑状态。
     */
    closeSpecification() {
      this.showSpecificationPopup = false;
      this.editingGoods = null;
      this.selectedSpecificationId = null;
      this.selectedRemarks = [];
      this.customRemark = "";
    },
    /**
     * 切换一条预设备注，可同时选择多条。
     */
    toggleRemark(remark) {
      const index = this.selectedRemarks.indexOf(remark);
      if (index >= 0) {
        this.selectedRemarks.splice(index, 1);
      } else {
        this.selectedRemarks.push(remark);
      }
    },
    /**
     * 将规格弹窗中的选择合并为购物车条目。
     * 相同菜品、规格和备注生成同一 key 并累加数量，不同备注会成为独立条目。
     */
    confirmSpecification() {
      if (!this.editingGoods || !this.selectedSpecificationId) return;
      const specification = this.specificationOptions.find(
        (item) => item.id === this.selectedSpecificationId
      );
      const remarkText = [...this.selectedRemarks, this.customRemark]
        .filter(Boolean)
        .join("、");
      this.addCartItem(this.editingGoods, specification, remarkText);
      this.closeSpecification();
    },
    /**
     * 为无规格菜品直接生成标准份购物车条目。
     */
    addSimpleGoods(goods) {
      this.addCartItem(
        goods,
        { id: "default", name: "标准份", price: Number(goods.Price || 0) },
        ""
      );
    },
    /**
     * 新增或累加购物车条目。
     *
     * @param {Object} goods 接口菜品对象。
     * @param {Object} specification 已选规格。
     * @param {string} remarkText 合并后的备注。
     */
    addCartItem(goods, specification, remarkText) {
      const goodsId = goods.Id || goods.GoodsCode;
      const key = `${goodsId}_${specification.id}_${remarkText}`;
      const existingItem = this.cartItems.find((item) => item.key === key);
      if (existingItem) {
        existingItem.quantity += 1;
        return;
      }
      this.cartItems.push({
        key,
        goodsId,
        catalogId: goods.GoodsCatalogId,
        name: goods.GoodsName,
        image: goods.PicUrl,
        specId: specification.id,
        specName: specification.name,
        price: specification.price,
        remarkText,
        quantity: 1,
      });
    },
    /**
     * 修改购物车条目数量；数量减到 0 时直接移除该条目。
     */
    changeCartQuantity(key, direction) {
      const index = this.cartItems.findIndex((item) => item.key === key);
      if (index < 0) return;
      const nextQuantity = this.cartItems[index].quantity + direction;
      if (nextQuantity <= 0) {
        this.cartItems.splice(index, 1);
      } else {
        this.cartItems[index].quantity = nextQuantity;
      }
    },
    /**
     * 清空新页面自己的购物车，不调用或修改项目原有购物车逻辑。
     */
    clearCart() {
      this.cartItems = [];
    },
    /**
     * 输出订单数据供后续提交页或接口使用。
     * 空购物车只提示，不触发事件；有效时同时携带条目、总数量和总金额。
     */
    handleSubmit() {
      if (!this.cartItems.length) {
        this.$toast("请先选择菜品");
        return;
      }
      this.$emit("submit-order", {
        items: this.cartItems.map((item) => ({ ...item })),
        totalCount: this.cartTotalCount,
        totalMoney: this.cartTotalMoney,
      });
    },
    /**
     * 跳转到项目已有订单记录路由。
     */
    openOrders() {
      this.$router.push({ name: "RecordsConsumption" });
    },
    /**
     * 将金额统一格式化为最多两位小数。
     */
    formatMoney(value) {
      return Number(value || 0).toFixed(2).replace(/\.00$/, "");
    },
  },
};
</script>

<style scoped lang="scss">
.black-order-page {
  min-height: 100vh;
  padding-bottom: calc(104px + env(safe-area-inset-bottom));
  color: #f8f8f6;
  background: #242424;
  color-scheme: dark;
}

.brand-hero {
  padding: 42px 24px 0;
  background: radial-gradient(circle at 78% 18%, #4e3a1c 0, #171717 40%, #111 100%);
}

.brand-copy {
  padding: 20px 0 24px;
  border-bottom: 1px solid #b98b3e;

  h1 {
    margin: 0;
    font-size: 34px;
    letter-spacing: 0;
  }

  p {
    margin: 10px 0 34px;
    color: #aaa7a2;
    font-size: 20px;
  }

  button {
    padding: 0;
    border: 0;
    color: #fff;
    background: transparent;
    font-size: 23px;
  }
}

.recommend-banner {
  position: relative;
  height: 300px;
  overflow: hidden;
  background: #111;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: brightness(0.48) sepia(0.25);
  }

  div {
    position: absolute;
    left: 18px;
    bottom: 18px;
    display: flex;
    flex-direction: column;
  }

  strong {
    font-size: 30px;
  }

  span {
    color: #c7c3bd;
    font-size: 18px;
  }
}

.search-box {
  position: relative;
  margin: 36px 24px 18px;

  input {
    width: 100%;
    height: 64px;
    padding: 0 24px 0 86px;
    border: 1px solid #e0b655;
    border-radius: 6px;
    box-sizing: border-box;
    color: #fff;
    background: #686868;
    font-size: 24px;
    outline: 0;

    &::placeholder {
      color: #e8e8e8;
    }
  }
}

.search-icon {
  position: absolute;
  z-index: 1;
  left: 28px;
  top: 16px;
  width: 29px;
  height: 29px;
  border: 2px solid #fff;
  border-radius: 4px;
}

.menu-layout {
  display: flex;
  align-items: flex-start;
}

.category-list {
  position: sticky;
  top: 0;
  width: 158px;
  flex: 0 0 158px;
  background: #1c1c1c;

  button {
    position: relative;
    width: 100%;
    min-height: 112px;
    padding: 18px 12px;
    border: 0;
    border-right: 4px solid transparent;
    color: #e5e5e2;
    background: #1c1c1c;
    font-size: 21px;

    &.active {
      border-right-color: #f2bf49;
      color: #f5cd65;
      background: #57452f;
    }
  }

  i {
    position: absolute;
    right: 12px;
    top: 12px;
    min-width: 28px;
    height: 28px;
    border-radius: 14px;
    color: #1d1d1d;
    background: #f2c75b;
    font-size: 16px;
    font-style: normal;
    line-height: 28px;
  }
}

.goods-panel {
  min-width: 0;
  padding: 18px;
  flex: 1;
  background: #333;
}

.category-heading {
  display: flex;
  margin: 4px 0 18px;
  align-items: baseline;

  strong {
    color: #f1c453;
    font-size: 28px;
  }

  span {
    margin-left: 10px;
    color: #99906f;
    font-size: 17px;
  }
}

.goods-card {
  margin-bottom: 20px;
  overflow: hidden;
  border-radius: 5px;
  background: #181818;
}

.goods-image {
  position: relative;
  height: 210px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.recommend-tag {
  position: absolute;
  left: 0;
  top: 18px;
  padding: 9px 16px;
  border-radius: 0 5px 5px 0;
  color: #362a12;
  background: #f3cd68;
  font-size: 18px;
}

.goods-info {
  display: flex;
  min-height: 88px;
  padding: 14px 16px;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;

  > div:first-child {
    display: flex;
    min-width: 0;
    flex-direction: column;
  }

  strong {
    overflow: hidden;
    font-size: 25px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    color: #9c9c9a;
    font-size: 16px;
  }
}

.goods-action {
  display: flex;
  align-items: center;

  p {
    margin: 0 14px 0 0;
    font-size: 26px;
    font-weight: 600;
    white-space: nowrap;
  }

  small {
    color: #989895;
    font-size: 15px;
  }

  button {
    height: 48px;
    padding: 0 16px;
    border: 0;
    border-radius: 5px;
    color: #32260e;
    background: #f3cf70;
    font-size: 20px;
  }

  .add-button {
    width: 48px;
    padding: 0;
    border-radius: 50%;
    font-size: 27px;
  }
}

.cart-bar {
  position: fixed;
  z-index: 20;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  height: calc(96px + env(safe-area-inset-bottom));
  padding: 0 24px env(safe-area-inset-bottom);
  align-items: center;
  background: #171717;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.28);
}

.cart-symbol {
  position: relative;
  width: 82px;
  color: #f1ce67;
  font-size: 47px;

  i {
    position: absolute;
    right: 9px;
    top: -4px;
    min-width: 27px;
    height: 27px;
    border-radius: 14px;
    color: #222;
    background: #f2c85c;
    font-size: 16px;
    font-style: normal;
    line-height: 27px;
    text-align: center;
  }
}

.cart-summary {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;

  strong {
    color: #f3ce69;
    font-size: 27px;
  }

  span {
    color: #dededb;
    font-size: 20px;
  }
}

.cart-bar > button,
.submit-button,
.spec-footer button {
  height: 68px;
  padding: 0 30px;
  border: 0;
  border-radius: 6px;
  color: #2c230e;
  background: linear-gradient(90deg, #d7aa42, #ffe78d);
  font-size: 27px;
  font-weight: 600;
}

.black-popup {
  max-height: 82vh;
  overflow: hidden;
  color: #f4f4f2;
  background: #242424;
}

.cart-popup,
.spec-popup {
  display: flex;
  max-height: 82vh;
  min-height: 430px;
  flex-direction: column;
  background: #242424;
}

.popup-header {
  display: flex;
  min-height: 84px;
  padding: 0 26px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #444;

  strong {
    font-size: 28px;
  }

  span {
    display: block;
    margin-top: 4px;
    color: #9d9d98;
    font-size: 17px;
  }

  button {
    border: 0;
    color: #e1bd5d;
    background: transparent;
    font-size: 22px;
  }
}

.cart-list,
.spec-content {
  min-height: 0;
  padding: 18px 24px;
  flex: 1;
  overflow-y: auto;
}

.cart-item {
  display: flex;
  min-height: 112px;
  padding: 14px 0;
  align-items: center;
  border-bottom: 1px solid #3d3d3d;

  img {
    width: 92px;
    height: 92px;
    border-radius: 5px;
    object-fit: cover;
  }
}

.cart-item-info {
  min-width: 0;
  margin-left: 16px;
  flex: 1;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    font-size: 23px;
  }

  span {
    color: #979792;
    font-size: 16px;
  }

  p {
    margin: 8px 0 0;
    color: #f1c75e;
    font-size: 22px;
  }
}

.quantity-control {
  display: flex;
  align-items: center;

  button {
    width: 42px;
    height: 42px;
    border: 1px solid #d9b655;
    border-radius: 50%;
    color: #e5c267;
    background: transparent;
    font-size: 24px;
  }

  span {
    min-width: 46px;
    text-align: center;
  }
}

.cart-total {
  display: flex;
  height: 66px;
  padding: 0 24px;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #414141;

  strong {
    color: #f1c75e;
    font-size: 24px;
  }
}

.popup-footer {
  display: flex;
  min-height: calc(94px + env(safe-area-inset-bottom));
  padding: 12px 24px env(safe-area-inset-bottom);
  justify-content: space-between;
  border-top: 1px solid #414141;

  button {
    width: 48%;
    height: 66px;
    border-radius: 6px;
    font-size: 25px;
  }
}

.close-button {
  border: 1px solid #caa64e;
  color: #e2bd60;
  background: transparent;
}

.submit-button:disabled {
  color: #777;
  background: #444;
}

.option-group {
  margin-bottom: 28px;

  h3 {
    margin: 0 0 14px;
    color: #f0c75d;
    font-size: 23px;
  }
}

.option-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  button {
    min-height: 54px;
    padding: 8px;
    border: 1px solid #555;
    border-radius: 5px;
    color: #dededb;
    background: #303030;
    font-size: 19px;

    span {
      display: block;
      color: #aaa;
      font-size: 15px;
    }

    &.active {
      border-color: #edc45c;
      color: #f4ca60;
      background: #4b4029;
    }
  }
}

.option-group textarea {
  width: 100%;
  height: 112px;
  padding: 14px;
  border: 1px solid #555;
  border-radius: 5px;
  box-sizing: border-box;
  color: #fff;
  background: #303030;
  font-size: 20px;
  outline: 0;
  resize: none;
}

.spec-footer {
  display: flex;
  min-height: calc(92px + env(safe-area-inset-bottom));
  padding: 12px 24px env(safe-area-inset-bottom);
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #414141;

  div {
    display: flex;
    flex-direction: column;
  }

  span {
    color: #aaa;
    font-size: 16px;
  }

  strong {
    color: #f2c85e;
    font-size: 28px;
  }
}

.state-message {
  padding: 80px 20px;
  color: #999;
  text-align: center;
}
</style>
